<template>
  <div class="relative">
    <h2>{{ t('depositCreditCard') }}</h2>

    <div class="mt-4 grid w-4/5 grid-cols-2 gap-x-6 lg:w-2/3">
      <LabeledText :label="t('openRentalFee')"
        ><span v-currency="displayedOpenRentalFee"></span
      ></LabeledText>
      <LabeledText :label="t('deposit')"
        ><span v-currency="booking.priceCalculation.deposit"></span
      ></LabeledText>
    </div>

    <div
      v-if="booking.bookedCar.vehicleType?.canBeBookedWithDebitCard"
      class="mt-2 text-sm text-green-700"
    >
      {{ t('debitCardDepositInfo') }}
    </div>
    <div v-else class="mt-2 text-sm text-red-700">
      {{ t('noDebitCardDepositInfo') }}
    </div>

    <Divider class="col-span-3" />

    <PaymentOptions
      v-if="mode === Mode.BLOCK"
      :booking="booking"
      :amount="totalAmount"
      :is-saving="isSavingBooking"
      mode="block"
      @charge-credit-card-via-terminal="blockCreditCardViaTerminal"
      @send-payment-link="sendBlockingPaymentLink"
      @continue-without-transaction="continueWithoutTransaction"
      @adyen-payment="
        (amount, paymentMethod) =>
          performAdyenPayment({
            amount,
            paymentMethod,
            blocking: true,
          })
      "
    />

    <div v-if="mode === Mode.CHARGE">
      <p class="mb-4">
        {{
          booking.collectDeposit
            ? unpaidDeposit > 0
              ? t('collectDepositCharge')
              : t('collectDepositAlreadyPaid')
            : t('depositBlockedButOpenFee')
        }}
      </p>

      <PaymentOptions
        :booking="booking"
        :amount="openRentalFee"
        :is-saving="isSavingBooking"
        mode="charge"
        @charge-credit-card-via-terminal="chargeCreditCardViaTerminal"
        @send-payment-link="sendPaymentLink"
        @continue-without-transaction="continueWithoutTransaction"
        @adyen-payment="
          (amount, paymentMethod) =>
            performAdyenPayment({
              amount,
              paymentMethod,
              blocking: false,
            })
        "
      />
    </div>

    <div v-if="mode === Mode.NOTHING_TO_BLOCK">
      <p class="mb-4 text-green-700">
        {{ t('nothingToBlock') }}
      </p>

      <div class="mt-5 flex justify-end">
        <ButtonNext @click.prevent="goToNextPage">
          {{ t('next') }}
        </ButtonNext>
      </div>
    </div>

    <div v-if="mode === Mode.CHARGED">
      <p class="mb-4">
        {{ t('depositIsAlreadyBlocked') }}
      </p>

      <div class="flex justify-end">
        <p class="col-start-3 mr-2 grow-0 text-right">
          <span class="lg:text-base"> {{ t('reservedAmount') }}: </span>
          <span
            v-currency="booking.blockedCreditCardAmount"
            class="block text-base font-bold md:text-2xl"
          />
        </p>
      </div>

      <div class="mt-5 flex justify-end">
        <ButtonNext @click.prevent="goToNextPage">
          {{ t('next') }}
        </ButtonNext>
      </div>
    </div>

    <div
      v-if="
        isInitiatingTerminal ||
        isWaitingForTransaction ||
        isChargingAmount ||
        isBlockingAmount ||
        isPerformingAdyenPayment
      "
      class="absolute -inset-8 flex items-center justify-center bg-black/20"
    >
      <div class="flex items-center gap-2 rounded-full bg-accent px-3 py-2">
        <p class="test-base lg:text-xl">{{ t(waitingForTransactionMessage) }}</p>
        <Spinner class="h-5 w-5 lg:h-7 lg:w-7" />
      </div>
      <CVButton
        v-if="isWaitingForPaymentLink"
        variant="danger"
        class="text-link absolute right-8 top-8 text-red-600"
        @click.prevent="cancelWaitForPaymentLinkCompletion"
      >
        {{ t('cancelPaymentLink') }}<XMarkIcon class="h-4 w-4" />
      </CVButton>
      <CVButton
        v-if="isPerformingAdyenPayment"
        variant="danger"
        class="absolute right-8 top-8"
        @click.prevent="cancelAdyenPayment"
      >
        {{ t('cancel') }}<XIcon class="size-4" />
      </CVButton>
    </div>
  </div>
</template>

<script lang="ts" setup>
import LabeledText from '@/components/LabeledText.vue';
import Divider from '@/components/Divider.vue';
import type { Booking } from '@/entities/bookings/booking.entity';
import { useI18n } from 'vue-i18n';
import { computed, onUnmounted, ref } from 'vue';
import ButtonNext from '@/components/buttons/ButtonNext.vue';
import { useProcessNavigation } from '@/hooks/use-process-step-tracking';
import PaymentOptions from './PaymentOptions.vue';
import type { ChargeCreditCardNotificationEvent } from '@/entities/payment-terminals/charge-credit-card-notification-event';
import { useChargeCreditCard } from '@/queries/use-payment-terminals';
import { TerminalTransactionType } from '@/entities/payment-terminals/terminal-transaction-type.enum';
import { paymentTerminalEventsService } from '@/api/payment-terminal-events.service';
import { ChargeCreditCardStatus } from '@/entities/payment-terminals/charge-credit-card-status.enum';
import { createToast } from 'mosha-vue-toastify';
import {
  useBlockAmount,
  useChargeAmount,
  useStoredPaymentMethods,
} from '@/queries/use-payment-api';
import { AutoChargeType } from '@/entities/payment-terminals/auto-charge-type.enum';
import {
  useSendDepositReceipt,
  useUpdateBooking,
  useEnsureBookingCustomerLinked,
} from '@/queries/use-bookings';
import { DateTime } from 'luxon';
import { XMarkIcon } from '@heroicons/vue/24/solid';
import { CounterpartyType, CreditCard } from '@carvia/ros-client-types';
import AdyenPlugin from '@/capacitor-plugins/AdyenPlugin';
import {
  abandonAdyenPosSession,
  AdyenPosBusyError,
  ensureAdyenPosSessionFor,
  initializeAdyenPayment,
} from '@/queries/use-adyen-payments';
import { XIcon } from 'lucide-vue-next';
import { useMutation } from '@tanstack/vue-query';
import * as Sentry from '@sentry/vue';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';
import { Alert } from '@/services/alert.service';

enum Mode {
  BLOCK = 'BLOCK',
  CHARGE = 'CHARGE',
  CHARGED = 'CHARGED',
  NOTHING_TO_BLOCK = 'NOTHING_TO_BLOCK',
}

const props = defineProps<{
  booking: Booking;
  isSaving?: boolean;
}>();

const { t } = useI18n();

const { mutateAsync: updateBooking, isPending: isSavingBooking } = useUpdateBooking();

const { mutateAsync: ensureCustomerLinked } = useEnsureBookingCustomerLinked();

const waitingForTransactionMessage = ref('');
const isWaitingForTransaction = ref(false);
const isWaitingForPaymentLink = ref(false);

const openRentalFee = computed(() => props.booking.currentBalance.CUSTOMER);
const unpaidDeposit = computed(() => Math.max(0, props.booking.unpaidDeposit ?? 0));
const displayedOpenRentalFee = computed(() => (openRentalFee.value ?? 0) - unpaidDeposit.value);
const deposit = computed(() =>
  Math.min(
    // if a deposit has already been blocked, don't charge any potential increase of the deposit
    props.booking.priceCalculation.deposit,
    props.booking.blockedCreditCardAmount || Number.MAX_VALUE,
  ),
);
const totalAmount = computed(
  () =>
    (props.booking.collectDeposit ? 0 : (deposit.value ?? 0)) +
    Math.max(openRentalFee.value ?? 0, 0),
);

const mode = computed(() => {
  if (totalAmount.value === 0) {
    return Mode.NOTHING_TO_BLOCK;
  } else if (props.booking.collectDeposit) {
    return Mode.CHARGE;
  } else if (!props.booking.blockedCreditCardDate) {
    return Mode.BLOCK;
  } else if (
    (props.booking.blockedCreditCardAmount ?? 0) <
    totalAmount.value - 0.001 /* floating point correction */
  ) {
    return Mode.CHARGE;
  } else {
    return Mode.CHARGED;
  }
});

const { mutateAsync: chargeCreditCard, isPending: isInitiatingTerminal } = useChargeCreditCard();

const { mutateAsync: chargeAmount, isPending: isChargingAmount } = useChargeAmount();

const { mutateAsync: sendDepositReceipt } = useSendDepositReceipt();

const { mutateAsync: blockAmount, isPending: isBlockingAmount } = useBlockAmount();

const eventsServiceMessages: ChargeCreditCardNotificationEvent[] = [];

let transactionRefNr = '';
const blockCreditCardViaTerminal = async (paymentTerminalId: string, amount: number) => {
  chargeCreditCardViaTerminal(paymentTerminalId, amount, TerminalTransactionType.BLOCK);
};

const chargeCreditCardViaTerminal = async (
  paymentTerminalId: string,
  amount: number,
  type = TerminalTransactionType.CHARGE,
) => {
  if (!paymentTerminalId) return;
  waitingForTransactionMessage.value = 'connecting';
  const res = await chargeCreditCard({
    amount: amount,
    paymentTerminalId: paymentTerminalId,
    bookingId: props.booking.id,
    type,
  });
  transactionRefNr = res.refNr;
  isWaitingForTransaction.value = true;
  eventsServiceMessages.forEach((event: ChargeCreditCardNotificationEvent) =>
    reactToEventsServiceMessages(event),
  );
};

type AdyenPaymentParams = {
  amount: number;
  paymentMethod: 'cardReader' | 'tapToPay';
  blocking: boolean;
};

/**
 * Runs the steps up to the native payment. Checks for cancel after every step: a cancelled flow
 * whose hung step resumes later must never go on to start a card payment.
 */
const startAdyenPayment = async (params: AdyenPaymentParams, signal: AbortSignal) => {
  const throwIfCanceled = () => {
    if (signal.aborted) {
      throw new AdyenPaymentCanceledError();
    }
  };
  waitingForTransactionMessage.value = 'loading';
  adyenPaymentProgress = {
    step: 'linkCustomer',
    startedAt: Date.now(),
    paymentMethod: params.paymentMethod,
  };
  transactionRefNr = Array(16)
    .fill(0)
    .map(() => String.fromCharCode(65 + Math.floor(Math.random() * 26)))
    .join('');

  const bookingWithCustomer = props.booking.customer
    ? props.booking
    : await ensureCustomerLinked(props.booking.id);
  throwIfCanceled();
  adyenPaymentProgress.step = 'openPosSession';
  await ensureAdyenPosSessionFor(props.booking).catch(throwBusyMessage);
  throwIfCanceled();
  adyenPaymentProgress.step = 'getMethods';

  const availableMethods = await AdyenPlugin.getMethods();
  throwIfCanceled();
  if (!availableMethods[params.paymentMethod]) {
    throw new Error(
      t(params.paymentMethod === 'cardReader' ? 'cardReaderUnavailable' : 'tapToPayUnavailable'),
    );
  }

  adyenPaymentProgress.step = 'initializePayment';
  return initializeAdyenPayment(
    {
      amount: params.amount,
      currency: 'EUR',
      reference: transactionRefNr,
      adyenData: {
        tenderOption: 'ReceiptHandler',
        metadata: {
          refNr: transactionRefNr,
          bookingId: bookingWithCustomer.id,
          paymentLinkType: params.blocking ? 'BOOKING_DEPOSIT' : 'BOOKING_PAYMENT',
          bookingNumber: bookingWithCustomer.bookingNumber.toString(),
          manualCapture: params.blocking ? 'true' : 'false',
          ...(!params.blocking && {
            autoAllocateLedger: 'true',
            counterpartyType: CounterpartyType.CUSTOMER,
          }),
        },
        shopperReference: bookingWithCustomer.customer?.id,
        shopperEmail: bookingWithCustomer.customer?.email,
        recurringProcessingModel: 'UnscheduledCardOnFile',
        additionalData: {
          authorisationType: 'PreAuth',
          manualCapture: params.blocking ? 'true' : 'false',
        },
      },
      method: params.paymentMethod,
    },
    props.booking.legalEntity.id,
  ).catch(throwBusyMessage);
};

const throwBusyMessage = (error: unknown): never => {
  throw error instanceof AdyenPosBusyError ? new Error(t('previousPaymentStillRunning')) : error;
};

class AdyenPaymentCanceledError extends Error {
  // Keeps the global mutation error toast away: the user cancelled on purpose
  __handled = true;
}

let adyenPaymentProgress: {
  step: 'linkCustomer' | 'openPosSession' | 'getMethods' | 'initializePayment';
  startedAt: number;
  paymentMethod: AdyenPaymentParams['paymentMethod'];
} = { step: 'linkCustomer', startedAt: 0, paymentMethod: 'tapToPay' };

let rejectAdyenPayment: (() => void) | null = null;

/**
 * Lets the user leave a payment stuck on loading (e.g. the POS session never opens). The native
 * call cannot be aborted, so its result is ignored and the POS session is opened afresh next time.
 */
const cancelAdyenPayment = () => {
  // We don't know yet where payments hang; report the step so we can find out
  Sentry.captureMessage('Adyen payment cancelled while loading', {
    level: 'warning',
    extra: {
      forceReport: true,
      step: adyenPaymentProgress.step,
      waitedMs: Date.now() - adyenPaymentProgress.startedAt,
      paymentMethod: adyenPaymentProgress.paymentMethod,
      platform: Capacitor.getPlatform(),
      bookingId: props.booking.id,
    },
  });
  rejectAdyenPayment?.();
  rejectAdyenPayment = null;
  abandonAdyenPosSession();
};

const { isPending: isPerformingAdyenPayment, mutate: performAdyenPayment } = useMutation({
  mutationFn: (params: AdyenPaymentParams) => {
    const controller = new AbortController();
    return Promise.race([
      startAdyenPayment(params, controller.signal),
      new Promise<never>((_, reject) => {
        rejectAdyenPayment = () => {
          controller.abort();
          reject(new AdyenPaymentCanceledError());
        };
      }),
    ]).finally(() => {
      rejectAdyenPayment = null;
    });
  },
  onSuccess: async (data) => {
    // Extract CustomerReceipt
    const receipts: any[] = data.SaleToPOIResponse?.PaymentResponse?.PaymentReceipt || [];
    const customerReceipt = receipts.find((r: any) => r.DocumentQualifier === 'CustomerReceipt')
      ?.OutputContent?.OutputText;

    // Ask user if they want to share the receipt before continuing
    if (customerReceipt?.length) {
      const shareReceipt = async () => {
        const { shouldSendMail, shouldShare, isDenied } = await Alert.fire({
          titleText: t('shareReceiptQuestion'),
          text: t('shareReceiptQuestionText'),
          icon: 'question',
          buttons: [
            {
              text: t('sendMail'),
              propertyName: 'shouldSendMail',
              style: 'default',
            },
            {
              text: t('share'),
              propertyName: 'shouldShare',
              style: 'secondary',
            },
            {
              text: t('no'),
              propertyName: 'isDenied',
              style: 'ghost',
            },
          ],
        });
        if (isDenied) {
          return;
        }
        const receiptText = customerReceipt
          .map((line: any) => {
            const params = new URLSearchParams(line.Text || '');
            const name = params.get('name');
            const value = params.get('value');
            const lineEnding = line.EndOfLineFlag ? '\n' : '';
            if (name && value) {
              return `${name}: ${value}${lineEnding}`;
            } else if (name) {
              return `${name}${lineEnding}`;
            }

            return lineEnding;
          })
          .join('');
        if (shouldSendMail) {
          await sendDepositReceipt({ bookingId: props.booking.id, receiptText: receiptText });
        } else if (shouldShare) {
          // Format receipt: decode URL and respect EndOfLineFlag

          try {
            await Share.share({
              title: t('customerReceipt'),
              text: receiptText,
              dialogTitle: t('shareReceipt'),
            });
          } catch (e) {
            // Retry if share fails
            await shareReceipt();
          }
        }
      };
      await shareReceipt();
    }
    if (data.SaleToPOIResponse?.PaymentResponse?.Response?.Result === 'Success') {
      goToNextPage();
    }
  },
});

paymentTerminalEventsService.open().subscribe({
  next(event) {
    eventsServiceMessages.push(event);
    isWaitingForTransaction.value && reactToEventsServiceMessages(event);
  },
  error(err) {
    console.error('something wrong occurred: ' + err);
  },
});

const reactToEventsServiceMessages = async (event: ChargeCreditCardNotificationEvent) => {
  // if payment terminal is not the one we are waiting for, ignore
  // Use transactionId in the future
  if (event.refNr === transactionRefNr) {
    switch (event.status) {
      case ChargeCreditCardStatus.REQUESTING:
        waitingForTransactionMessage.value = 'followInstructionsOnTerminal';
        break;
      case ChargeCreditCardStatus.REQUESTED:
        waitingForTransactionMessage.value = 'transactionIsCompleted';
        break;
      case ChargeCreditCardStatus.SUCCESS:
        isWaitingForTransaction.value = false;
        await updateBooking({
          id: props.booking.id,
          booking: { blockedCreditCardIncludesOpenFee: true },
        });
        goToNextPage();
        break;
      case ChargeCreditCardStatus.FAILED:
        isWaitingForTransaction.value = false;
        createToast(t('transactionCanceled'), {
          position: 'bottom-right',
          type: 'danger',
        });
    }
  }
};

onUnmounted(() => paymentTerminalEventsService.close());

const { data: storedPaymentMethods } = useStoredPaymentMethods(props.booking.id);

const chargeFromSavedCreditCard = async (amount: number, type = AutoChargeType.PSEUDO_NUMBER) => {
  waitingForTransactionMessage.value =
    type === AutoChargeType.PSEUDO_NUMBER
      ? 'chargingAmountOnSavedCreditCard'
      : 'sendingPaymentLink';
  const res = await chargeAmount({
    bookingId: props.booking.id,
    types: [type],
    amount,
    counterpartyType: CounterpartyType.CUSTOMER,
    autoAllocateLedger: true,
    key:
      storedPaymentMethods.value?.find(({ purposes }) => purposes.includes(CreditCard.PERIODIC))
        ?.id ??
      storedPaymentMethods.value?.find(({ purposes }) =>
        purposes.includes(CreditCard.ONLINE_PAYMENT),
      )?.id ??
      storedPaymentMethods.value?.find(({ purposes }) => purposes.includes(CreditCard.DEPOSIT))?.id,
  });
  if (res.paymentLink) {
    transactionRefNr = res.paymentLink.refNr;
    isWaitingForTransaction.value = true;
    waitingForTransactionMessage.value = 'waitingForPaymentLinkCompletion';
  }
  if (res.charged) {
    goToNextPage();
  }
};

const cancelWaitForPaymentLinkCompletion = () => {
  isWaitingForTransaction.value = false;
  isWaitingForPaymentLink.value = false;
  transactionRefNr = '';
};

const sendBlockingPaymentLink = async (amount: number) => {
  waitingForTransactionMessage.value = 'sendingPaymentLink';
  const res = await blockAmount({
    amount,
    bookingId: props.booking.id,
  });
  if (res.paymentLink) {
    transactionRefNr = res.paymentLink.refNr;
    isWaitingForTransaction.value = true;
    waitingForTransactionMessage.value = 'waitingForPaymentLinkCompletion';
  }
  isWaitingForPaymentLink.value = true;
};

const sendPaymentLink = (amount: number) => {
  chargeFromSavedCreditCard(amount, AutoChargeType.PAYMENT_LINK);
  isWaitingForPaymentLink.value = true;
};

const continueWithoutTransaction = async (amount: number) => {
  if (mode.value === Mode.BLOCK) {
    await updateBooking({
      id: props.booking.id,
      booking: {
        blockedCreditCardAmount: amount,
        blockedCreditCardDate: DateTime.now().toISODate(),
        blockedCreditCardIncludesOpenFee: true,
      },
    });
  } else if (mode.value === Mode.CHARGE) {
    await updateBooking({
      id: props.booking.id,
      booking: {
        blockedCreditCardIncludesOpenFee: true,
      },
    });
  }
  goToNextPage();
};

const { goToNext: goToNextPage } = useProcessNavigation();
</script>

<i18n lang="json">
{
  "en": {
    "depositCreditCard": "Deposit Credit Card",
    "openRentalFee": "Open Rental Fee",
    "deposit": "Deposit",
    "connecting": "Connecting",
    "followInstructionsOnTerminal": "Please follow instructions on terminal",
    "transactionIsCompleted": "Completing transaction",
    "transactionCanceled": "The transaction was canceled. Please try again, if this was not intended.",
    "depositIsAlreadyBlocked": "The total amount of deposit and open rental price has already been blocked on the credit card.",
    "depositBlockedButOpenFee": "The deposit has been blocked on the credit card, but a (partial) rent fee is still open.",
    "collectDepositCharge": "The deposit is charged and will be returned to you after the rental.",
    "collectDepositAlreadyPaid": "The deposit has already been charged. A (partial) rent fee is still open.",
    "reservedAmount": "Reserved Amount",
    "blockingAmountOnSavedCreditCard": "Blocking amount on saved credit card",
    "sendingPaymentLink": "Sending payment link",
    "waitingForPaymentLinkCompletion": "Waiting for payment link completion",
    "chargingAmountOnSavedCreditCard": "Charging amount on saved credit card",
    "cancelPaymentLink": "Cancel payment link",
    "cancel": "Cancel",
    "previousPaymentStillRunning": "A cancelled payment for another company is still running in the background. Please restart the app.",
    "shareReceiptQuestion": "Would you like to share the customer receipt?",
    "shareReceiptQuestionText": "Do you want to share the customer receipt now?",
    "yes": "Yes",
    "no": "No",
    "customerReceipt": "Customer Receipt",
    "shareReceipt": "Share Receipt",
    "shareFailed": "Sharing the receipt failed. Please try again.",
    "debitCardDepositInfo": "Note: This deposit can be paid with a credit / debit / EC card.",
    "noDebitCardDepositInfo": "Note: This deposit must be paid with a credit card.",
    "nothingToBlock": "No deposit or rental fees are required.",
    "cardReaderUnavailable": "No card reader available. Please connect the card reader and try again.",
    "tapToPayUnavailable": "Tap to Pay is not available on this device right now. Please try again or use another payment method.",
    "sendMail": "Send mail",
    "share": "Share ..."
  },
  "de": {
    "depositCreditCard": "Kreditkarte hinterlegen",
    "openRentalFee": "Noch offener Mietbetrag",
    "deposit": "Kaution",
    "connecting": "Verbindung wird aufgebaut",
    "followInstructionsOnTerminal": "Bitte folge den Anweisungen auf dem Terminal",
    "transactionIsCompleted": "Transaktion wird abgeschlossen",
    "transactionCanceled": "Die Transaktion wurde abgebrochen. Bitte versuche es nochmal, falls das nicht beabsichtigt war.",
    "depositIsAlreadyBlocked": "Der Gesamtbetrag aus Kaution und offenem Mietpreis wurde bereits auf der Kreditkarte blockiert.",
    "depositBlockedButOpenFee": "Die Kaution wurde auf der Kreditkarte blockiert, aber ein (Teil-)Mietbetrag ist noch offen.",
    "collectDepositCharge": "Die Kaution wird eingezogen und nach der Rückgabe des Fahrzeugs zurückerstattet.",
    "collectDepositAlreadyPaid": "Die Kaution wurde bereits eingezogen. Ein (Teil-)Mietbetrag ist noch offen.",
    "reservedAmount": "Reservierter Betrag",
    "blockingAmountOnSavedCreditCard": "Betrag auf hinterlegter Kreditkarte blockieren",
    "sendingPaymentLink": "Zahlungslink wird gesendet",
    "waitingForPaymentLinkCompletion": "Warte auf Zahlungslink Abschluss",
    "chargingAmountOnSavedCreditCard": "Betrag wird auf hinterlegter Kreditkarte eingezogen",
    "cancelPaymentLink": "Zahlungslink abbrechen",
    "cancel": "Abbrechen",
    "previousPaymentStillRunning": "Eine abgebrochene Zahlung für eine andere Firma läuft im Hintergrund noch. Bitte starte die App neu.",
    "shareReceiptQuestion": "Möchten Sie den Kundenbeleg teilen?",
    "shareReceiptQuestionText": "Möchten Sie den Kundenbeleg jetzt teilen?",
    "yes": "Ja",
    "no": "Nein",
    "customerReceipt": "Kundenbeleg",
    "shareReceipt": "Belege teilen",
    "shareFailed": "Teilen des Beleges fehlgeschlagen. Bitte versuchen Sie es erneut.",
    "debitCardDepositInfo": "Hinweis: Diese Kaution kann mit einer Kredit- / Debit- / EC-Karte hinterlegt werden.",
    "noDebitCardDepositInfo": "Hinweis: Diese Kaution muss mit einer Kreditkarte hinterlegt werden.",
    "nothingToBlock": "Keine Kaution oder Mietgebühren erforderlich.",
    "cardReaderUnavailable": "Kein Kartenleser verfügbar. Bitte verbinde den Kartenleser und versuche es erneut.",
    "tapToPayUnavailable": "Tap to Pay ist auf diesem Gerät gerade nicht verfügbar. Bitte versuche es erneut oder nutze eine andere Zahlungsart.",
    "sendMail": "E-Mail senden",
    "share": "Teilen ..."
  },
  "es": {
    "depositCreditCard": "Tarjeta para la fianza",
    "openRentalFee": "Importe de alquiler pendiente",
    "deposit": "Fianza",
    "connecting": "Conectando",
    "followInstructionsOnTerminal": "Siga las instrucciones del terminal",
    "transactionIsCompleted": "Completando la transacción",
    "transactionCanceled": "La transacción se ha cancelado. Inténtelo de nuevo si no era su intención.",
    "depositIsAlreadyBlocked": "El importe total de fianza y alquiler pendiente ya está bloqueado en la tarjeta de crédito.",
    "depositBlockedButOpenFee": "La fianza está bloqueada en la tarjeta de crédito, pero aún queda un importe (parcial) de alquiler pendiente.",
    "collectDepositCharge": "La fianza se cobra y se devuelve después de la devolución del vehículo.",
    "collectDepositAlreadyPaid": "La fianza ya se ha cobrado. Aún queda un importe (parcial) de alquiler pendiente.",
    "reservedAmount": "Importe reservado",
    "blockingAmountOnSavedCreditCard": "Bloqueando importe en la tarjeta guardada",
    "sendingPaymentLink": "Enviando enlace de pago",
    "waitingForPaymentLinkCompletion": "Esperando la finalización del enlace de pago",
    "chargingAmountOnSavedCreditCard": "Cobrando importe en la tarjeta guardada",
    "cancelPaymentLink": "Cancelar enlace de pago",
    "cancel": "Cancelar",
    "previousPaymentStillRunning": "Un pago cancelado de otra empresa sigue en curso en segundo plano. Reinicie la aplicación.",
    "shareReceiptQuestion": "¿Desea compartir el ticket del cliente?",
    "shareReceiptQuestionText": "¿Quiere compartir ahora el ticket del cliente?",
    "yes": "Sí",
    "no": "No",
    "customerReceipt": "Ticket del cliente",
    "shareReceipt": "Compartir ticket",
    "shareFailed": "No se pudo compartir el ticket. Inténtelo de nuevo.",
    "debitCardDepositInfo": "Nota: esta fianza puede pagarse con tarjeta de crédito / débito / EC.",
    "noDebitCardDepositInfo": "Nota: esta fianza debe pagarse con tarjeta de crédito.",
    "nothingToBlock": "No se requiere fianza ni cargos de alquiler.",
    "cardReaderUnavailable": "No hay ningún lector de tarjetas disponible. Conecte el lector e inténtelo de nuevo.",
    "tapToPayUnavailable": "Tap to Pay no está disponible en este dispositivo en este momento. Inténtelo de nuevo o use otro método de pago.",
    "sendMail": "Enviar correo",
    "share": "Compartir…"
  }
}
</i18n>
